import { FC, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Field, Form, FormSpy } from 'react-final-form';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LoadingButton,
    SelectChangeEvent,
    SelectFinalField,
} from '@thirdspacelearning/library';
import { required } from '@tsl-frontend/tsl-utilities';

import {
    useCreateProgrammeRecommendation,
    useUpdateProgrammeRecommendation,
} from '../../hooks';
import {
    ProgrammeRecommendation,
    ProgrammeType,
    WorkingLevel,
} from '../../types';
import {
    getWorkingLevelOptions,
    gradeOptions,
    yearGroupOptions,
} from '../../utils';
import { RecommendationCopyKeys, createRecommendationCopy } from '../../copy';

import { getProgrammes } from '@api';
import { CurriculumQueryKeys } from '@services';
import { Country } from '@types';

const sx = {
    dialog: {
        '& .MuiDialog-paper': {
            p: 3,
            gap: 2,
        },
    },
    dialogTitle: {
        p: 0,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    dialogContent: {
        p: 0,
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '450px',
    },
    dialogActions: {
        display: 'flex',
    },
};

type Props = {
    open: boolean;
    onClose: (show: boolean) => void;
    country: Country;
    recommendation?: ProgrammeRecommendation;
};

type FormValues = {
    programme_id?: string;
    year_id?: string;
    working_level?: string;
};

const ProgrammeRecommendationForm: FC<Props> = ({
    open,
    onClose,
    country,
    recommendation,
}) => {
    const [isPristine, setPristine] = useState(true);

    const { data: programmes } = useQuery<ProgrammeType[]>({
        queryKey: [CurriculumQueryKeys.PROGRAMMES, country],
        queryFn: () => getProgrammes(country),
        refetchOnWindowFocus: false,
    });

    const programmeOptions = useMemo(
        () =>
            (programmes ?? []).map((programme) => ({
                id: String(programme.id),
                name: programme.title,
            })),
        [programmes],
    );

    const yearOptions =
        country === Country.GB ? yearGroupOptions : gradeOptions;
    const yearLabel = country === Country.GB ? 'Year group *' : 'Grade *';

    const { createRecommendation, isCreating } =
        useCreateProgrammeRecommendation(() => onClose(false));
    const { updateRecommendation, isUpdating } =
        useUpdateProgrammeRecommendation(recommendation?.id, () =>
            onClose(false),
        );

    const onSubmit = (values: FormValues) => {
        const payload: Partial<ProgrammeRecommendation> = {
            programme_id: Number(values.programme_id),
            year_id: Number(values.year_id),
            working_level: values.working_level as WorkingLevel,
        };

        if (recommendation) {
            updateRecommendation(payload);
        } else {
            createRecommendation(payload);
        }
    };

    const handleClose = (_event: object, reason: string) => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose(false);
    };

    const initialValues: FormValues = recommendation
        ? {
              programme_id: String(recommendation.programme_id),
              year_id: String(recommendation.year_id),
              working_level: recommendation.working_level,
          }
        : {};

    const isPending = isCreating || isUpdating;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            sx={sx.dialog}
            data-testid="recommendation-form"
        >
            <DialogTitle sx={sx.dialogTitle}>
                {recommendation
                    ? createRecommendationCopy(
                          RecommendationCopyKeys.EDIT_TITLE,
                      )
                    : createRecommendationCopy(
                          RecommendationCopyKeys.CREATE_TITLE,
                      )}
            </DialogTitle>
            <DialogContent sx={sx.dialogContent}>
                <Form
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    render={({ handleSubmit, values, form }) => (
                        <form
                            onSubmit={handleSubmit}
                            id="recommendationForm"
                            noValidate
                        >
                            <Box sx={sx.formContainer}>
                                <Field
                                    name="year_id"
                                    component={SelectFinalField}
                                    label={yearLabel}
                                    options={yearOptions}
                                    variant="outlined"
                                    validate={required}
                                    required
                                    fullWidth
                                    parse={(value) =>
                                        value != null ? String(value) : ''
                                    }
                                    format={(value) =>
                                        value != null ? String(value) : ''
                                    }
                                    onChangeCallback={(
                                        event: SelectChangeEvent,
                                    ) => {
                                        const nextYearId = event.target.value
                                            ? Number(event.target.value)
                                            : undefined;
                                        const stillValid =
                                            getWorkingLevelOptions(
                                                country,
                                                nextYearId,
                                            ).some(
                                                (option) =>
                                                    option.id ===
                                                    values.working_level,
                                            );

                                        if (!stillValid) {
                                            form.change(
                                                'working_level',
                                                undefined,
                                            );
                                            form.resetFieldState(
                                                'working_level',
                                            );
                                        }
                                    }}
                                    data-testid="year-input"
                                />
                                <Field
                                    name="working_level"
                                    component={SelectFinalField}
                                    label="Working level *"
                                    options={getWorkingLevelOptions(
                                        country,
                                        values.year_id
                                            ? Number(values.year_id)
                                            : undefined,
                                    )}
                                    variant="outlined"
                                    validate={required}
                                    required
                                    fullWidth
                                    disabled={!values.year_id}
                                    data-testid="working-level-input"
                                />
                                <Field
                                    name="programme_id"
                                    component={SelectFinalField}
                                    label="Programme *"
                                    options={programmeOptions}
                                    variant="outlined"
                                    validate={required}
                                    required
                                    fullWidth
                                    parse={(value) =>
                                        value != null ? String(value) : ''
                                    }
                                    format={(value) =>
                                        value != null ? String(value) : ''
                                    }
                                    data-testid="programme-input"
                                />
                            </Box>
                            <FormSpy
                                subscription={{ pristine: true }}
                                onChange={({ pristine }) => {
                                    if (!pristine) {
                                        setPristine(false);
                                    }
                                }}
                            />
                        </form>
                    )}
                />
            </DialogContent>
            <DialogActions sx={sx.dialogActions}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => onClose(false)}
                    fullWidth
                >
                    Close
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    form="recommendationForm"
                    fullWidth
                    loading={isPending}
                    disabled={isPristine}
                    data-testid="recommendation-submit-button"
                >
                    {recommendation
                        ? createRecommendationCopy(
                              RecommendationCopyKeys.SUBMIT_EDIT,
                          )
                        : createRecommendationCopy(
                              RecommendationCopyKeys.SUBMIT_CREATE,
                          )}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ProgrammeRecommendationForm;
